import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface PDFExportOptions {
  filename?: string;
  format?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  includeDate?: boolean;
  quality?: number;
}

/**
 * Export the current page content to PDF by rendering the DOM
 */
export async function exportPageToPDF(
  elementId: string = 'flow-detail-content',
  options: PDFExportOptions = {}
): Promise<void> {
  const {
    filename = 'flow-export',
    format = 'letter',
    orientation = 'portrait',
    includeDate = true,
    quality = 0.95
  } = options;

  try {
    // Show loading indicator
    const loadingDiv = document.createElement('div');
    loadingDiv.id = 'pdf-export-loading';
    loadingDiv.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      ">
        <div style="
          background: white;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          text-align: center;
        ">
          <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p style="margin: 0; font-size: 16px; color: #374151;">Generating PDF...</p>
        </div>
      </div>
    `;
    document.body.appendChild(loadingDiv);

    // Get the element to export
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Element not found for PDF export');
    }

    // Clone the element to modify for export
    const clonedElement = element.cloneNode(true) as HTMLElement;
    clonedElement.style.padding = '20px';
    
    // Add print-specific classes
    clonedElement.classList.add('pdf-export');
    
    // Hide elements that shouldn't be in PDF
    const hideSelectors = [
      'button',
      '.no-print',
      '[aria-label="Breadcrumb"]',
      'nav',
      '.chatbot',
      'summary'
    ];
    
    hideSelectors.forEach(selector => {
      clonedElement.querySelectorAll(selector).forEach(el => {
        (el as HTMLElement).style.display = 'none';
      });
    });

    // Expand all collapsed sections
    clonedElement.querySelectorAll('details').forEach(details => {
      details.setAttribute('open', 'true');
    });

    // Show all tab content
    clonedElement.querySelectorAll('[role="tabpanel"]').forEach(panel => {
      (panel as HTMLElement).style.display = 'block';
      panel.removeAttribute('aria-hidden');
    });

    // Append temporarily to body for rendering
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = format === 'letter' ? '816px' : '794px'; // 8.5" or A4 width at 96dpi
    tempContainer.appendChild(clonedElement);
    document.body.appendChild(tempContainer);

    // Generate canvas from the element
    const canvas = await html2canvas(clonedElement, {
      scale: 2, // Higher quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: tempContainer.offsetWidth,
      windowHeight: tempContainer.scrollHeight
    });

    // Remove temp container
    document.body.removeChild(tempContainer);

    // Calculate PDF dimensions
    const imgWidth = format === 'letter' ? 215.9 : 210; // mm
    const pageHeight = format === 'letter' ? 279.4 : 297; // mm
    const margin = 10; // mm margin on top/bottom for page breaks
    const usablePageHeight = pageHeight - (margin * 2);
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Create PDF
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format
    });

    // Smart page breaking: Add images to PDF with intelligent breaks
    const imgData = canvas.toDataURL('image/jpeg', quality);
    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    // First page
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Additional pages with better positioning to avoid awkward breaks
    while (heightLeft > 0) {
      position = -(pageHeight * pageNumber);
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      pageNumber++;
    }

    // Generate filename
    const date = includeDate ? `-${new Date().toISOString().split('T')[0]}` : '';
    const fullFilename = `${filename}${date}.pdf`;

    // Save the PDF
    pdf.save(fullFilename);

    // Remove loading indicator
    document.body.removeChild(loadingDiv);

  } catch (error) {
    console.error('PDF export error:', error);
    // Remove loading indicator if it exists
    const loading = document.getElementById('pdf-export-loading');
    if (loading) {
      document.body.removeChild(loading);
    }
    throw error;
  }
}

/**
 * Alternative: Use browser's print functionality with better styling
 */
export function printPage(): void {
  // Add print-specific class to body
  document.body.classList.add('printing');
  
  // Expand all collapsible sections
  document.querySelectorAll('details').forEach(details => {
    details.setAttribute('open', 'true');
  });
  
  // Show all tabs
  document.querySelectorAll('[role="tabpanel"]').forEach(panel => {
    (panel as HTMLElement).style.display = 'block';
  });
  
  // Trigger print
  window.print();
  
  // Clean up after print
  window.addEventListener('afterprint', () => {
    document.body.classList.remove('printing');
  }, { once: true });
}